import dayjs from 'dayjs';
import { TranslationKey } from '../translation/TranslationKey';
import { t, translate } from '../translation/Translator';
import {
  CustomFieldEntityType,
  CustomFieldEntitySubType,
  CustomFieldType,
  CustomFieldEntityChildType,
  CustomFieldEntityParentType,
  CustomFieldValue,
  CustomField,
} from './customField';
import { User } from '../user';
import { Contact } from '../contact';
import { ProductMasterData } from '../product_master_data/productMasterData';
import { FileEntityType } from '../file';

export namespace CustomFieldUtil {
  export function customFieldEntityTypeToString(field: CustomFieldEntityType): string {
    switch (field) {
      case CustomFieldEntityType.order:
        return t(TranslationKey.order).singular.label;
      case CustomFieldEntityType.productTransaction:
        return t(TranslationKey.productTransaction).singular.label;
      case CustomFieldEntityType.containerTransaction:
        return t(TranslationKey.containerTransaction).singular.label;
      case CustomFieldEntityType.productMasterData:
        return t(TranslationKey.productReferenceData).singular.label;
      case CustomFieldEntityType.lot:
        return t(TranslationKey.lot).singular.label;
      case CustomFieldEntityType.bin:
        return t(TranslationKey.bin).singular.label;
      case CustomFieldEntityType.container:
        return t(TranslationKey.container).singular.label;
      case CustomFieldEntityType.product:
        return t(TranslationKey.product).singular.label;
      case CustomFieldEntityType.asset:
        return t(TranslationKey.asset).singular.label;
      case CustomFieldEntityType.companyRole:
        return t(TranslationKey.user).singular.label;
      case CustomFieldEntityType.contact:
        return t(TranslationKey.contact).singular.label;
      case CustomFieldEntityType.assetTransaction:
        return t(TranslationKey.assetTransaction).singular.label;
    }
  }

  export function customFieldEntitySubtypeToString(field?: CustomFieldEntitySubType): string {
    if (!field) return '';
    switch (field) {
      case CustomFieldEntitySubType.inbound:
        return translate({ key: TranslationKey.inbound });
      case CustomFieldEntitySubType.outbound:
        return translate({ key: TranslationKey.outbound });
      case CustomFieldEntitySubType.move:
        return translate({ key: TranslationKey.move });
      case CustomFieldEntitySubType.replenish:
        return translate({ key: TranslationKey.replenish });
      case CustomFieldEntitySubType.disposal:
        return translate({ key: TranslationKey.disposal });
    }
  }

  export function customFieldTypeToString(field: CustomFieldType): string {
    switch (field) {
      case CustomFieldType.bool:
        return 'Boolean';
      case CustomFieldType.date:
        return 'Date';
      case CustomFieldType.text:
        return 'Text';
      case CustomFieldType.listOfValues:
        return 'List of Values';
      case CustomFieldType.file:
        return t(TranslationKey.file).singular.label;
      case CustomFieldType.listOfTuples:
        return 'List of Tuples';
      case CustomFieldType.number:
        return 'Number';
      case CustomFieldType.image:
        return t(TranslationKey.image).singular.label;
    }
  }

  export function customFieldEntityChildTypeToString(type?: CustomFieldEntityChildType) {
    if (!type) return '';
    switch (type) {
      case CustomFieldEntityChildType.container:
        return t(TranslationKey.container).singular.label;
    }
  }

  export function customFieldEntityParentTypeToLocalizedString(entityParentType?: CustomFieldEntityParentType): string {
    if (!entityParentType) return '';
    switch (entityParentType) {
      case CustomFieldEntityParentType.order:
        return t(TranslationKey.order).singular.label;
      case CustomFieldEntityParentType.ownership:
        return t(TranslationKey.ownership).singular.label;
    }
  }

  export function getTextForCustomField(customFieldValue: CustomFieldValue, locale: string): string {
    switch (customFieldValue.type) {
      case CustomFieldType.bool:
        return customFieldValue.value === 'true' ? 'True' : 'False';
      case CustomFieldType.date:
        const date = dayjs(customFieldValue.value);
        if (date.isValid()) {
          return date.toDate().toLocaleDateString(locale);
        } else {
          return '';
        }
      case CustomFieldType.listOfTuples:
        return customFieldValue.value.includes(':::') ? customFieldValue.value.split(':::')[0] : customFieldValue.value;
      case CustomFieldType.image:
      case CustomFieldType.file:
      case CustomFieldType.number:
      case CustomFieldType.listOfValues:
      case CustomFieldType.text:
        return customFieldValue.value;
    }
  }

  export function getReferenceItems(
    cf: CustomField,
    users: Map<string, User>,
    contacts: Map<string, Contact>,
    productMasterData: Map<string, ProductMasterData>,
  ): any[] {
    switch (cf.reference) {
      case CustomFieldEntityType.companyRole:
        return [...users.values()].map(item => item.email);
      case CustomFieldEntityType.contact:
        return [...contacts.values()].map(item => item.name);
      case CustomFieldEntityType.productMasterData:
        return [...productMasterData.values()].map(item => item.productNumber);
      default:
        return [];
    }
  }

  export function getReferenceText(cf: CustomField, item: any): string {
    switch (cf.reference) {
      case CustomFieldEntityType.companyRole:
        return (item as User).email;
      default:
        return item;
    }
  }

  export function getFileEntityType(cf: CustomField): FileEntityType | undefined {
    switch (cf.entityType) {
      case CustomFieldEntityType.productMasterData:
        return FileEntityType.pmd;
      case CustomFieldEntityType.assetTransaction:
        return FileEntityType.assetTransaction;
      case CustomFieldEntityType.order:
        return FileEntityType.order;
    }
  }
}
